import { useLocation } from 'react-router';

import { IS_PROD_APP_ENV } from 'Utilities/constants';
import { INCODE_EARLY_DOCS_SLUG, INCODE_POST_SALE_SLUG } from 'Utilities/routing';

const INCODE_DOMAIN = IS_PROD_APP_ENV ? 'saaseu-api' : 'demo-api';

export const API_URL = `https://${INCODE_DOMAIN}.incodesmile.com/0/`;

export const INCODE_ROUTES = {
  CAPTURE_LICENSE: '/capture-license',
  COMPLETE: '/complete',
  FSL: '/finance-settlement-letter',
  PROOF_OF_ADDRESS: '/proof-of-address',
  SELFIE: '/selfie',
  SHARE_AUTHORISATION_LINK: '/share-authorisation-link',
  V5C: '/V5C',
};

export const POST_INCODE_ROUTE = '/purchasing-dealer-details';

export const INCODE_PROGRESS_STATUS = {
  FAILED: 'failed',
  SUCCESS: 'successful',
} as const;

export const INCODE_PROGRESS_STEP = {
  CONSENT: 'consent',
  DRIVING_LICENCE: 'driving_licence',
  FINANCE: 'finance_settlement_letter',
  FINGERPRINT: 'fingerprint',
  KEEPER_AUTHORIZATION: 'keeper_authorization_link',
  PROOF_OF_ADDRESS: 'poa',
  SELFIE: 'selfie',
  V5: 'v5',
} as const;

export const SENTRY_TAG = {
  tags: {
    feature: 'IncodeSDK',
  },
};

export const INCODE_BE_ROUTES: Partial<Record<
  typeof INCODE_PROGRESS_STEP[keyof typeof INCODE_PROGRESS_STEP],
  string
>> = {
  [INCODE_PROGRESS_STEP.CONSENT]: '',
  [INCODE_PROGRESS_STEP.DRIVING_LICENCE]: INCODE_ROUTES.CAPTURE_LICENSE,
  [INCODE_PROGRESS_STEP.FINANCE]: INCODE_ROUTES.FSL,
  [INCODE_PROGRESS_STEP.KEEPER_AUTHORIZATION]: INCODE_ROUTES.SHARE_AUTHORISATION_LINK,
  [INCODE_PROGRESS_STEP.SELFIE]: INCODE_ROUTES.SELFIE,
  [INCODE_PROGRESS_STEP.V5]: INCODE_ROUTES.V5C,
  [INCODE_PROGRESS_STEP.PROOF_OF_ADDRESS]: INCODE_ROUTES.PROOF_OF_ADDRESS,
};

export type IncodeProgressStatus = {
  status: typeof INCODE_PROGRESS_STATUS[keyof typeof INCODE_PROGRESS_STATUS];
  step: typeof INCODE_PROGRESS_STEP[keyof typeof INCODE_PROGRESS_STEP];
};

export type IncodeSDK = {
  [key: string]: (...args: any[]) => any;
};

export type IncodeSession = {
  completedSteps: string[] | null;
  consentId: string;
  consentedAt?: string;
  createdAt: string;
  documentsUploaded: boolean;
  drivingLicenceUploaded: boolean;
  enquiryId: string;
  flowId: string;
  id: string;
  interviewCode: string;
  interviewId: string;
  isDocsEarly: boolean;
  modules: Record<string, {
    rejectedAt: string | null;
    status: string;
    submittedAt: string | null;
    verifiedAt: string | null;
  }>;
  onboardingLink?: string;
  provider: string;
  requiredSteps: string[];
  saleId?: string;
  saleVerificationId: string;
  sellerId: string;
  token: string;
  type: string;
  updatedAt: string;
  v5Uploaded: boolean;
  verificationExpiresAt: string;
  verificationStartedAt: string;
  verificationStatus: string;
};

export type IncodeSessionAPI = Omit<IncodeSession, 'token'> & {
  onboardingToken: string;
};

export type IncodeSessionAPIResponse = {
  createdAt: string;
  externalId: string;
  id: string;
  saleId: string;
  sellerVerificationSession: IncodeSessionAPI;
  status: string;
  updatedAt: string;
};

export type IncodeStepProps = {
  BASE_URL: string;
  DP_ENQUIRY_ID: number;
  incodeSDK: IncodeSDK;
  incodeSession?: IncodeSession;
};

export const useIncodeSlug = () => {
  const { pathname } = useLocation();

  return pathname.includes(INCODE_EARLY_DOCS_SLUG) ? INCODE_EARLY_DOCS_SLUG : INCODE_POST_SALE_SLUG;
};
